<template>
  <div ref="calendar-timeslot-picker" class="calendar-timeslot-picker">
    <div class="day-row">
      <div
        class="arrow arrow--left"
        :class="{ 'arrow--disabled': isPreviousWeekDisabled }"
        @click="previousWeek()"
      >
        <span />
      </div>
      <div class="day-row__container">
        <div
          :class="{
            'days-container--start': displayStart,
            'days-container--start-with-transition': displayStartWithTransition,
            'days-container--end': displayEnd,
          }"
          class="days-container"
        >
          <div
            v-for="(element, index) in availableDays"
            :key="index"
            class="day"
            :class="{ 'day--disabled': isDayBeforeNow(element.fullDate),
                      'day--has-selection': hasSelectedSlots(element),
                      'day--current': selectedDay === element.fullDate,
            }"
            @click="setSelectedDay(element)"
          >
            <div class="day__text">
              <span>{{ element.dayInWeek }}</span>
              <div v-if="hasSelectedSlots(element)" class="pin">
                {{ getNumberSelectedSlotsPerDay(element) }}
              </div>
            </div>
            <span class="day__number">{{ element.day }}/{{ element.month }}</span>
          </div>
        </div>
      </div>
      <div class="arrow arrow--right" @click="nextWeek()">
        <span />
      </div>
    </div>
    <div class="time-pick">
      <div class="time-pick__halfday time-pick__morning halfday">
        <span class="halfday__title">
          {{ trans('calendar.moment.morning') }}
        </span>
        <div class="halfday__group">
          <span
            v-for="(morningTime, index) in isLongPeriodEnabled ? longMorning : shortMorning"
            :key="index"
            class="halfday__slot"
            :class="{
              'halfday__slot--disabled': maxSlotsReached,
              'halfday__slot--selected': isSlotSelected(morningTime)
            }"
            @click="selectSlot(morningTime)"
          >
            {{ morningTime }}
          </span>
        </div>
      </div>
      <div class="time-pick__halfday time-pick__afternoon">
        <span class="halfday__title">
          {{ trans('calendar.moment.afternoon') }}
        </span>
        <div class="halfday__group">
          <span
            v-for="(afternoonTime, index) in isLongPeriodEnabled ? longAfternoon : shortAfternoon"
            :key="index"
            class="halfday__slot"
            :class="{
              'halfday__slot--disabled': maxSlotsReached,
              'halfday__slot--selected': isSlotSelected(afternoonTime) }"
            @click="selectSlot(afternoonTime)"
          >
            {{ afternoonTime }}
          </span>
        </div>
      </div>
    </div>
    <div
      class="extend-slots"
      @click="onClickExtend()"
    >
      {{ trans(isLongPeriodEnabled ? 'calendar.slots.see_less' : 'calendar.slots.see_more') }}
    </div>
    <span
      v-for="(selectedSlot, i) in selectedSlots"
      :key="i"
      style="display: none"
    >
      <input
        v-model="selectedSlot.formattedDay"
        :name="`schedule_deal[date][${i}]`"
      >
      <input
        v-model="selectedSlot.time"
        :name="`schedule_deal[time][${i}]`"
      >
    </span>
  </div>
</template>

<script>
import moment from 'moment';
import { storeToRefs } from 'pinia';
import { useScreensizeStore } from '@stores/useScreensizeStore.ts';
import translation from '@elements/translation';
import eventBus from '@tools/event-bus';

export const MAX_SLOTS = 10;

export default {
  mixins: [ translation ],
  emits: [ 'time-changed', 'updated', 'update-next-button' ],
  setup() {
    const { isMobile } = storeToRefs(useScreensizeStore());

    return {
      isMobile,
    };
  },
  data() {
    return {
      MAX_SLOTS,
      today: null,
      selectedDay: null,
      availableDays: [],
      shortMorning: [],
      shortAfternoon: [],
      longMorning: [],
      longAfternoon: [],
      isLongPeriodEnabled: false,
      selectedSlots: [],
      displayEnd: false,
      displayStart: false,
      displayStartWithTransition: false,
    };
  },
  computed: {
    interval() {
      if (this.startTime.HH && this.startTime.mm) {
        const time = moment(`${this.startTime.HH}:${this.startTime.mm}`, 'HH:mm');
        return `${time.format('HH:mm')}-${time.add(30, 'minutes').format('HH:mm')}`;
      }

      return null;
    },
    isPreviousWeekDisabled() {
      if (!this.availableDays || !this.availableDays[0]) {
        return null;
      }
      return moment(this.availableDays[0].fullDate, 'DD-MM-YYYY').add(-30, 'minutes')
        .isBefore(moment().startOf('day'));
    },
    maxSlotsReached() {
      return this.selectedSlots.length >= this.MAX_SLOTS;
    },
    nbrDaySlotsToDisplay() {
      return this.isMobile ? 1 : 5;
    },
  },

  watch: {
    initialTime(value) {
      this.startTime = value ?? {
        HH: null,
        mm: null,
      };
    },
    startTime() {
      this.$emit('time-changed', this.startTime);
    },
    selectedSlots: {
      handler(val) {
        this.$emit('updated', val);
      },
      deep: true,
    },
  },
  mounted() {
    this.resizeHandler();
    this.updateLabelNextButton();
    let firstDayToDisplay = this.getFirstDayToDisplay(moment().add(1, 'days'));
    firstDayToDisplay = this.isMobile ? firstDayToDisplay.startOf('day') : firstDayToDisplay.startOf('week');
    this.getXNextDays(firstDayToDisplay, this.nbrDaySlotsToDisplay, true);
    // Tous les créneaux de 30 minutes entre 8h et 14h
    this.shortMorning = this.loadShortHours(30, 8, 14);
    // Tous les créneaux de 15 minutes entre 8h et 14h
    this.longMorning = this.loadShortHours(15, 8, 14);
    // Tous les créneaux de 30 minutes entre 14h et 20h
    this.shortAfternoon = this.loadShortHours(30, 14, 20);
    // Tous les créneaux de 15 minutes entre 14h et 20h
    this.longAfternoon = this.loadShortHours(15, 14, 20);

    eventBus.addEventListener('window-size-changed', this.resizeHandler);
  },
  methods: {
    getFirstDayToDisplay(day) {
      return !this.isWeekDay(day) ? this.getFirstDayToDisplay(day.add(1, 'days')) : day;
    },
    resizeHandler() {
      if (this.selectedDay) {
        const saveSelectedDay = this.selectedDay;
        const firstDayToDisplay = this.isMobile ? moment(this.selectedDay, 'DD-MM-YYYY').startOf('day')
          : moment(this.selectedDay, 'DD-MM-YYYY').startOf('week').add(1, 'days');
        this.getXNextDays(firstDayToDisplay, this.nbrDaySlotsToDisplay);
        this.selectedDay = saveSelectedDay;
      }
    },
    onClickExtend() {
      this.isLongPeriodEnabled = !this.isLongPeriodEnabled;
    },
    hasSelectedSlots(day) {
      return !!this.getNumberSelectedSlotsPerDay(day);
    },
    getNumberSelectedSlotsPerDay(day) {
      return this.selectedSlots.filter((v) => v.day === day.fullDate).length;
    },
    isSlotSelected(time) {
      return this.selectedSlots.findIndex((v) => v.code === `${time}${this.selectedDay}`) !== -1;
    },
    selectSlot(time) {
      if (this.isSlotSelected(time)) {
        this.selectedSlots = this.selectedSlots.filter((v) => v.code !== `${time}${this.selectedDay}`);
        this.updateLabelNextButton();
        return;
      }

      if (this.maxSlotsReached) {
        return;
      }

      this.selectedSlots.push({
        code: `${time}${this.selectedDay}`,
        time,
        day: this.selectedDay,
        formattedDay: this.selectedDay.replaceAll('-', '/'),
      });

      this.updateLabelNextButton();
    },
    updateLabelNextButton() {
      this.$emit('update-next-button', this.selectedSlots);
    },
    setSelectedDay(day) {
      if (this.isDayBeforeNow(day.fullDate)) {
        return;
      }
      this.selectedDay = day.fullDate;
    },
    isDayBeforeNow(date) {
      return moment(date, 'DD-MM-YYYY').isBefore(moment().startOf('day'));
    },
    getFormattedDateObject(date) {
      const formated = date.format('DD-MM-YYYY');
      return {
        fullDate: formated,
        dayInWeek: date.locale('fr').format('dddd').substring(0, 3),
        day: formated.slice(0, 2),
        month: formated.slice(3, 5),
        year: formated.slice(6, 10),
      };
    },
    getXNextDays(firstDay, nbrDays = 5, isMounting = false) {
      this.availableDays = [];
      const originalLength = this.availableDays.length;
      let currentDay = firstDay;
      while (this.availableDays.length < originalLength + nbrDays) {
        if (this.isWeekDay(currentDay)) {
          this.availableDays.push(this.getFormattedDateObject(currentDay));
        }
        currentDay = currentDay.add(1, 'days');
      }

      this.selectFirstAvailableDayOfCurrentWeek(isMounting);
    },
    selectFirstAvailableDayOfCurrentWeek(isMounting) {
      for (let i = 0; i < this.availableDays.length; i++) {
        if (!this.isDayBeforeNow(this.availableDays[i].fullDate)) {
          if (isMounting && i < this.availableDays.length - 1 && this.isWeekDay(moment()) && moment().isoWeekday() !== 5) {
            this.selectedDay = this.availableDays[i + 1].fullDate;
            return;
          }
          this.selectedDay = this.availableDays[i].fullDate;
          return;
        }
      }
    },
    /** Works only when the parameter day is a moment day. It is based on our french locale, so 5 and 6 are saturday and sunday */
    isWeekDay(day) {
      return day.isoWeekday() !== 6 && day.isoWeekday() !== 7;
    },
    nextWeek() {
      const daysToJump = this.isMobile ? 1 : 7;
      let dayNextPeriod = moment(this.availableDays[0].fullDate, 'DD-MM-YYYY')
        .add(daysToJump, 'days');

      let daysToAdd = this.isMobile ? 1 : 5;
      while (daysToAdd > 0) {
        if (dayNextPeriod.day() > 0 && dayNextPeriod.day() < 6) {
          this.availableDays.push(this.getFormattedDateObject(dayNextPeriod));
          daysToAdd--;
        }
        dayNextPeriod = dayNextPeriod.add(1, 'days');
      }

      this.displayEnd = true;
      setTimeout(() => {
        this.availableDays.splice(0, this.isMobile ? 1 : 5);
        this.selectedDay = this.availableDays[0].fullDate;
        this.displayEnd = false;
      }, 300);
    },
    previousWeek() {
      if (this.isPreviousWeekDisabled) {
        return;
      }

      this.displayStart = true;

      const daysToJump = this.isMobile ? 1 : 3;
      let dayPreviousPeriod = moment(this.availableDays[0].fullDate, 'DD-MM-YYYY')
        .add(-daysToJump, 'days');

      let daysToAdd = this.isMobile ? 1 : 5;
      while (daysToAdd > 0) {
        if (dayPreviousPeriod.day() > 0 && dayPreviousPeriod.day() < 6) {
          this.availableDays.unshift(this.getFormattedDateObject(dayPreviousPeriod));
          daysToAdd--;
        }
        dayPreviousPeriod = dayPreviousPeriod.subtract(1, 'days');
      }
      this.displayStartWithTransition = true;

      setTimeout(() => {
        this.availableDays.splice(this.isMobile ? 1 : 5);
        this.selectFirstAvailableDayOfCurrentWeek(false);
        this.displayStartWithTransition = false;
        this.displayStart = false;
      }, 300);
    },
    loadShortHours(step, beginningInHours, endInHours) {
      let beginningInMinutes = beginningInHours * 60;
      const endInMinutes = endInHours * 60;
      const times = []; // time array
      for (let i = 0; beginningInMinutes < endInMinutes; i++) {
        const hh = Math.floor(beginningInMinutes / 60); // getting hours of day in 0-24 format
        const mm = (beginningInMinutes % 60); // getting minutes of the hour in 0-55 format
        times[i] = `${(`0${hh}`).slice(-2)}:${(`0${mm}`).slice(-2)}`; // pushing data in array in [00:00 - 12:00 AM/PM format]
        beginningInMinutes += step;
      }

      return times;
    },
  },
};
</script>
